// Loads + caches the per-tool config. Exposes loading/error/password-required
// states so the UI can render LoadingView, PasswordPrompt, ErrorView, or the
// real screen without juggling the fetch lifecycle inline.

import { useEffect, useState } from 'react';
import {
  ensureMediaSession,
  getConfig,
  setSessionLang,
  setSessionPassword,
  setUnauthorizedHandler,
} from '@/lib/api';
import { applyBranding, setDocumentTitle } from '@/lib/branding';
import {
  configLoadFailure,
  passwordForTool,
  resolveLoadedConfig,
  statusAfterUnauthorized,
  type ConfigStatus,
} from '@/lib/toolConfigFlow';
import { useSessionStore } from '@/stores/session';

export type { ConfigStatus } from '@/lib/toolConfigFlow';

export function useToolConfig(toolId: string | undefined, lang?: string): {
  status: ConfigStatus;
  submitPassword(pw: string): void;
  lastAttemptFailed: boolean;
  /** True, wenn eine Talent-Route mitten in der Session 401 lieferte — der
   *  Admin hat das Passwort geändert; die Abfrage erscheint erneut. */
  passwordChanged: boolean;
} {
  const setLoaded = useSessionStore((s) => s.setLoaded);
  const [status, setStatus] = useState<ConfigStatus>({ kind: 'loading' });
  // Eingegebenes Passwort, gebunden an das Projekt, für das es getippt wurde:
  // Die Route `/:id` behält den Hook-State bei SPA-Navigation zu einem
  // anderen Projekt — ohne die Bindung ginge A's Passwort an B (gezählter
  // Fehlversuch, Integrations-Review 2026-09-24).
  const [typed, setTyped] = useState<{ toolId: string | undefined; pw: string } | null>(null);
  const password = passwordForTool(typed, toolId);
  // Zähler pro Abschicken: dasselbe (falsche) Passwort erneut abzuschicken
  // muss einen neuen Request auslösen — `password` allein als Dependency
  // änderte sich dann nicht, der Hinweis verschwand und nichts passierte
  // (Audit, Low).
  const [attempt, setAttempt] = useState(0);
  // Tracks whether the most recent password submission was rejected. Set
  // when a 401 fires after `password` was non-empty; cleared as soon as
  // the user types a new attempt.
  const [lastAttemptFailed, setLastAttemptFailed] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  useEffect(() => {
    if (!toolId) {
      setStatus({ kind: 'error', message: 'Missing tool ID', status: 400 });
      return;
    }
    let cancelled = false;
    setStatus({ kind: 'loading' });

    getConfig(toolId, password, lang)
      .then(async (config) => {
        if (cancelled) return;
        const resolved = resolveLoadedConfig(config, lang);
        if (!resolved.ok) {
          setStatus(resolved.status);
          return;
        }
        setSessionLang(resolved.sessionLang);
        const effective = resolved.effective;
        // Remember the validated password so api.ts can attach it to the
        // server's now-password-gated /tts, /sts and /upload calls. Undefined
        // for tools with no password → header never sent.
        setSessionPassword(password);
        // Stream-Cookie holen, BEVOR 'ready' den VideoPlayer mountet — sonst
        // läuft dessen erster Request ohne Session in ein 401 + Retry.
        await ensureMediaSession(toolId);
        if (cancelled) return;
        applyBranding(effective);
        setDocumentTitle(effective.project);
        setLoaded(effective, toolId);
        setLastAttemptFailed(false);
        setPasswordChanged(false);
        setStatus({ kind: 'ready', config: effective });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 after a password attempt = wrong password. 401 on the
        // initial bare load just means "this project requires one".
        const failure = configLoadFailure(err, !!password);
        if (failure.attemptFailed) setLastAttemptFailed(true);
        setStatus(failure.status);
      });

    // Die Media-Session gilt 12 h; bei sehr langen Sitzungen ohne Reload
    // rechtzeitig erneuern, damit auch die Audio-Streams (VersionsBar) nicht
    // in ein 401 laufen. Vor dem ersten erfolgreichen Load antwortet der
    // Dienst mit 401 — ensureMediaSession wirft nie, das ist folgenlos.
    const refresh = window.setInterval(() => { void ensureMediaSession(toolId); }, 6 * 3600 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(refresh);
    };
  }, [toolId, password, lang, attempt, setLoaded]);

  // Passwort mitten in der Session vom Admin geändert: /tts, /sts, /upload …
  // antworten 401 → zurück zur Passwortabfrage statt „Generation failed".
  // Der Session-Store bleibt stehen; nach dem neuen Passwort lädt die Config
  // neu und die Arbeitsansicht kommt zurück.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setPasswordChanged(true);
      setLastAttemptFailed(false);
      setStatus(statusAfterUnauthorized);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  return {
    status,
    submitPassword: (pw) => {
      setLastAttemptFailed(false);
      setTyped({ toolId, pw });
      setAttempt((a) => a + 1);
    },
    lastAttemptFailed,
    passwordChanged,
  };
}
